import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";

const LINES = ["DELEGATION", "WITHOUT LOSS", "OF CONTROL."];
const FLOW_LABELS = [
  ["PROPOSED", "proposed"],
  ["ROUTED TO HUMAN", "routed"],
  ["EXECUTING", "executing"],
  ["MEASURED", "measured"],
];

export default function Hero() {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const [flow, setFlow] = useState({ proposed: 1204, routed: 86, executing: 412, measured: 9318 });
  useEffect(() => {
    const t = setInterval(() => {
      setFlow((f) => ({
        proposed: f.proposed + 1 + Math.floor(Math.random() * 4),
        routed: f.routed + (Math.random() < 0.35 ? 1 : 0),
        executing: f.executing + Math.floor(Math.random() * 3),
        measured: f.measured + 2 + Math.floor(Math.random() * 6),
      }));
    }, 1800);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-end overflow-hidden pt-32" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/26765065/pexels-photo-26765065.png"
          alt=""
          className="h-full w-full object-cover opacity-[0.09]"
        />
        <div className="grid-bg absolute inset-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/80" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="mx-auto w-full max-w-[1600px] px-6 pb-10 lg:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-6 font-mono text-xs uppercase tracking-[0.35em] text-[#00FF41]"
          data-testid="hero-eyebrow"
        >
          Commercial Decision Infrastructure
        </motion.div>

        <div className="mb-10 flex flex-wrap gap-x-10 gap-y-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500" data-testid="hero-status-bar">
          <span><span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-[#00FF41] glow-dot" />SYS: ONLINE</span>
          <span>POLICY ENGINE: V14 ACTIVE</span>
          <span>OUT-OF-POLICY EXECUTIONS: <span className="text-[#00FF41]">0</span></span>
        </div>

        <h1 className="font-display font-extrabold leading-[0.92] tracking-tighter text-[clamp(3rem,10vw,10rem)]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ type: "spring", stiffness: 60, damping: 16, delay: 0.35 + i * 0.14 }}
              >
                {i === 2 ? (
                  <>
                    OF <span className="text-[#00FF41]">CONTROL.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-12 grid gap-10 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6"
          >
            <p className="max-w-xl font-mono text-sm leading-relaxed text-zinc-300" data-testid="hero-subcopy">
              Axiosera lets enterprises safely delegate commercial decisions to AI.
              PriceStrike, our first product, applies that infrastructure to pricing: generating
              recommendations, enforcing approved policy, executing accepted decisions and measuring
              the commercial outcome.
            </p>
            <p className="mt-6 max-w-xl font-mono text-xs leading-relaxed text-zinc-500" data-testid="hero-supporting-line">
              Starting with pricing gives Axiosera a high-frequency, measurable decision stream through
              which customers can progress safely from human review to bounded autonomy.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-start gap-6 lg:col-span-6 lg:items-end"
          >
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="hero-see-pricestrike-button"
                onClick={() => lenis?.scrollTo("#product", { offset: -72, duration: 1.6 })}
                className="bg-[#00FF41] px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-white"
              >
                See PriceStrike →
              </button>
              <button
                data-testid="hero-investor-thesis-button"
                onClick={() => lenis?.scrollTo("#investors", { offset: -72, duration: 1.6 })}
                className="border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-[#00FF41] hover:text-[#00FF41]"
              >
                Investor thesis
              </button>
            </div>
            <div className="grid w-full max-w-md grid-cols-2 border border-white/10 lg:grid-cols-4" data-testid="hero-decision-flow">
              {FLOW_LABELS.map(([label, key]) => (
                <div key={key} className="border-r border-white/10 px-3 py-3 last:border-r-0">
                  <div className="overflow-hidden font-display text-xl font-bold text-white">
                    <motion.span
                      key={flow[key]}
                      initial={{ y: 12, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="block"
                    >
                      {flow[key].toLocaleString()}
                    </motion.span>
                  </div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</div>
                </div>
              ))}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600" data-testid="hero-flow-live-tag">
              <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-[#00FF41] glow-dot" />
              Live governed decision stream
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.4 }}
          className="mt-16 flex flex-col gap-2 border-t border-white/10 pt-6 font-mono text-[11px] uppercase tracking-[0.25em] lg:flex-row lg:items-center lg:gap-6"
          data-testid="hero-expansion-strip"
        >
          <span className="text-[#00FF41]">Today: Pricing</span>
          <span className="hidden text-zinc-700 lg:inline">→</span>
          <span className="text-zinc-500">Next: Promotions · Markdown · Inventory allocation · Rebates · Discount authority</span>
        </motion.div>
      </motion.div>
    </section>
  );
}
